import Moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

const rollingPreviewClasses = [
  'DateRangePicker__Date--rolling-preview',
  'DateRangePicker__Date--rolling-preview-start',
  'DateRangePicker__Date--rolling-preview-end',
];
const calendarDecorationClasses = [
  'DateRangePicker__Date--range-start',
  'DateRangePicker__Date--range-end',
  'DateRangePicker__Date--pending',
  'DateRangePicker__DateHighlighted',
  ...rollingPreviewClasses,
];

interface CalendarDateElement {
  date: Moment.Moment;
  element: HTMLElement;
}

type CalendarRoot = HTMLElement;

interface ApplyCalendarDecorationsOptions {
  calendarRoot: CalendarRoot;
  normalizedAdditionDates: Set<string>;
  inProgressAnchor?: Date;
  pendingDate?: Date;
  rollingPreviewRange?: DateRange;
  valueRange: DateRange;
}

export const applyCalendarDecorations = ({
  calendarRoot,
  normalizedAdditionDates,
  inProgressAnchor,
  pendingDate,
  rollingPreviewRange,
  valueRange,
}: ApplyCalendarDecorationsOptions) => {
  clearCalendarDecorations(calendarRoot);

  const calendarDates = getCalendarDateElements(calendarRoot);
  const range = inProgressAnchor ? getInProgressRange({ calendarDates, inProgressAnchor }) : valueRange;

  markRangeEndpoints({ calendarDates, range });
  if (pendingDate) markPendingDate({ calendarDates, pendingDate });
  markAdditionDays({ calendarDates, normalizedAdditionDates });
  renderRollingRangePreview({ calendarDates, range: rollingPreviewRange });
};

const getCalendarMonth = (monthElement: HTMLElement) => {
  const monthText = monthElement.querySelector('.DateRangePicker__MonthHeader')?.textContent?.trim();
  if (!monthText) return undefined;

  const monthMatch = monthText.match(/(\w+)\s+(\d{4})/);
  if (!monthMatch) return undefined;

  const [, monthName, yearText] = monthMatch;
  const year = parseInt(yearText, 10);
  const parsedMonth = moment(`${monthName} 1, ${year}`, ['MMM D, YYYY', 'MMMM D, YYYY'], true);
  if (parsedMonth.isValid()) return parsedMonth.startOf('month');

  const fallbackMonth = moment().month(monthName);
  return fallbackMonth.isValid() ? fallbackMonth.year(year).startOf('month') : undefined;
};

const getCalendarDateElements = (calendarRoot: CalendarRoot) => {
  const calendarDates: CalendarDateElement[] = [];

  calendarRoot.querySelectorAll<HTMLElement>('.DateRangePicker__Month').forEach((monthElement) => {
    const month = getCalendarMonth(monthElement);
    if (!month) return;

    monthElement.querySelectorAll<HTMLElement>('.DateRangePicker__Date').forEach((dateElement) => {
      if (dateElement.classList.contains('DateRangePicker__Date--otherMonth')) return;

      const dayText = dateElement.querySelector('.DateRangePicker__DateLabel')?.textContent;
      const day = dayText ? parseInt(dayText, 10) : NaN;
      if (isNaN(day) || day < 1 || day > month.daysInMonth()) return;

      calendarDates.push({ date: month.clone().date(day).startOf('day'), element: dateElement });
    });
  });

  return calendarDates;
};

const clearCalendarDecorations = (calendarRoot: CalendarRoot) => {
  calendarRoot.querySelectorAll<HTMLElement>('.DateRangePicker__Date').forEach((element) => {
    element.classList.remove(...calendarDecorationClasses);
  });
};

// The legacy picker loses its range anchor after a rolling restart, so this paints its hover preview.
const renderRollingRangePreview = ({
  range,
  calendarDates,
}: {
  range?: DateRange;
  calendarDates: CalendarDateElement[];
}) => {
  if (!range) return;

  calendarDates.forEach(({ date, element }) => {
    if (!range.contains(date)) return;

    element.classList.add('DateRangePicker__Date--rolling-preview');
    if (date.isSame(range.start, 'day')) element.classList.add('DateRangePicker__Date--rolling-preview-start');
    if (date.isSame(range.end, 'day')) element.classList.add('DateRangePicker__Date--rolling-preview-end');
  });
};

const markPendingDate = ({
  pendingDate,
  calendarDates,
}: {
  pendingDate: Date;
  calendarDates: CalendarDateElement[];
}) => {
  calendarDates.forEach(({ date, element }) => {
    if (date.isSame(pendingDate, 'day')) element.classList.add('DateRangePicker__Date--pending');
  });
};

const markRangeEndpoints = ({ range, calendarDates }: { range: DateRange; calendarDates: CalendarDateElement[] }) => {
  calendarDates.forEach(({ date, element }) => {
    if (date.isSame(range.start, 'day')) element.classList.add('DateRangePicker__Date--range-start');
    if (date.isSame(range.end, 'day')) element.classList.add('DateRangePicker__Date--range-end');
  });
};

const getInProgressRange = ({
  inProgressAnchor,
  calendarDates,
}: {
  inProgressAnchor: Date;
  calendarDates: CalendarDateElement[];
}) => {
  // The desktop first click lives in the legacy picker; its selected cells reveal the other endpoint.
  const selectedDates = calendarDates.filter(({ element }) =>
    element.classList.contains('DateRangePicker__Date--is-selected'),
  );

  if (selectedDates.length === 0) {
    return moment.range(inProgressAnchor, inProgressAnchor);
  }

  const sortedSelectedDates = selectedDates
    .map(({ date }) => date)
    .sort((first, second) => first.valueOf() - second.valueOf());
  const firstSelectedDate = sortedSelectedDates[0];
  const lastSelectedDate = sortedSelectedDates[sortedSelectedDates.length - 1];

  if (moment(inProgressAnchor).isBefore(firstSelectedDate, 'day')) {
    return moment.range(inProgressAnchor, lastSelectedDate);
  }
  if (moment(inProgressAnchor).isAfter(lastSelectedDate, 'day')) {
    return moment.range(firstSelectedDate, inProgressAnchor);
  }

  return moment.range(firstSelectedDate, lastSelectedDate);
};

/** Normalizes highlighted dates to the date-only keys used by calendar decorations. */
export const normalizeHighlightedDates = (highlightedDates: Array<string | Date>): Set<string> => {
  return new Set(
    highlightedDates
      .map((date) => {
        try {
          const dateObject = typeof date === 'string' ? new Date(date) : date;
          return moment(dateObject).startOf('day').format('YYYY-MM-DD');
        } catch (error) {
          console.warn('Error normalizing addition date:', date, error);
          return null;
        }
      })
      .filter((date): date is string => date !== null),
  );
};

/**
 * Marks calendar days with addition indicators based on highlighted dates
 */
const markAdditionDays = ({
  normalizedAdditionDates,
  calendarDates,
}: {
  normalizedAdditionDates: Set<string>;
  calendarDates: CalendarDateElement[];
}) => {
  calendarDates.forEach(({ date, element }) => {
    const isRangeEdge =
      element.classList.contains('DateRangePicker__Date--range-start') ||
      element.classList.contains('DateRangePicker__Date--range-end');

    // Additions remain visible inside a range, except when they are an endpoint.
    if (normalizedAdditionDates.has(date.format('YYYY-MM-DD')) && !isRangeEdge) {
      element.classList.add('DateRangePicker__DateHighlighted');
    }
  });
};
